import React from 'react';
import { FancyButton } from '../../../../components/primitives/FancyButton';
import { getDataTableByCanonicalType } from '../../charts/chartDataBindings';
import { SingleEntityTemplate } from '../../../../api/models/assets';
import { TpDataContext, TpPostFetchCallback } from '../../../../data/interfaces';
import { showErrorMessage } from '../../../../components/message-boxes/ErrorMessage';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { addNewAsset, selectQueriedAssets } from '../../../../data/assets/queriedAssetsSlice';
import { TpAssetInfo } from '../../../../data/assets/interfaces';
import { useOpenAsset } from '../../../../data/assets/helpers';
import { convertColumnDataListToDisplayString, convertColumnDataToDisplayString } from '../../helpers/misc';
import executeDashboardAction from '../../actions/executeDashboardAction';
import { TpSingleEntityViewContext } from '../interfaces';
import { TpDashboardContext } from '../../dashboard/interfaces';
import { DBACTIONVARTYPES } from '../../actions/interfaces';
import { getColumnInfo, performUserSelectionAction } from '../../../../data/analyticsDataSlice';
import { getUserError } from '../../../../components/miscUtils';
import { useApiContext } from '../../../../api';
import { Api, TpAssetQueryParams } from '../../../../api/models/apis';
import { PresignedBlobData } from '../../../../api/models/data';
import { promptPdfViewer } from '../../../../components/pdf-viewer/PdfViewer';
import { analyticsDataStorage } from '../../../../data/analyticsDataStorage';
import { TpEnSelectionFilterTypes } from '../../../../data/selection/interfaces';
import { entityIsCompatible, formatDataTableStringTemplate } from './utils';

function findAssetByProperty(
  allAssets: TpAssetInfo[],
  propertyId: string,
  propertyValue: string,
  validAssetTypes: string[],
) {
  let filteredAssets = allAssets;
  if (validAssetTypes.length > 0) {
    filteredAssets = allAssets.filter((asset) => validAssetTypes.includes(asset.type));
  }
  for (const asset of filteredAssets) {
    if (asset.properties[propertyId] == propertyValue) return asset;
  }
  return null;
}

interface TpActionContext {
  dispatch: any;
  dashboardContext: TpDashboardContext;
  openAssetByProperty: (propertyId: string, propertyValue: string, api: Api, validAssetTypes: string[]) => void;
  openAssetById: (assetId: string) => void;
  closeEntityPopup: () => void;
  openEntity: (canonicalTypeId: string, entityId: string) => void;
  openAssetEntity: (propertyId: string, propertyValue: string, api: Api, assetType: string, itemId: string) => void;
}

interface TpActionDef {
  type: string;
  name: string;
  icon: string;
  actionId: string;
  requiredProperty?: string; // the property that needs to be present and have a value in order to render the actionButton
  requiredPropertyValues?: string[]; // the value that the requiredProperty should have in order to render the actionButton
}

function actionOpenUrl(
  actionContext: TpActionContext,
  actionDef: TpActionDef,
  dataContext: TpDataContext,
  entityTypeId: string,
  itemId: string,
  api: Api,
) {
  let url = (actionDef as any).url;
  if (!url) throw getUserError('Action is missing url parameter');
  const dataTable = getDataTableByCanonicalType(dataContext, entityTypeId);
  url = formatDataTableStringTemplate(url, dataTable, itemId);
  window.open(url, '_blank', 'noreferrer');
}

function actionOpenPdf(
  actionContext: TpActionContext,
  actionDef: TpActionDef,
  dataContext: TpDataContext,
  entityTypeId: string,
  itemId: string,
  api: Api,
) {
  let url = (actionDef as any).url;
  if (!url) throw getUserError('Action is missing url parameter');
  const dataTable = getDataTableByCanonicalType(dataContext, entityTypeId);
  url = formatDataTableStringTemplate(url, dataTable, itemId);
  const blobData: PresignedBlobData = {
    method: 'GET',
    blobPath: url,
    isDisplay: true,
  };
  api.blob.getPresignedURL(blobData).then((response) => {
    if (!response.signedUrl) {
      showErrorMessage('Failed to get signed URL');
      return;
    }
    promptPdfViewer(response.signedUrl);
  });
}

function getAssetFieldValue(actionDef: TpActionDef, dataContext: TpDataContext, entityTypeId: string, itemId: string) {
  const dataTable = getDataTableByCanonicalType(dataContext, entityTypeId);
  if (!(itemId in dataTable.itemIdMap)) {
    showErrorMessage(`Entity not found: ${itemId}`);
  }
  const itemIdx = dataTable.itemIdMap[itemId];
  const entityProperty = (actionDef as any).entityProperty;
  const fieldValue = convertColumnDataToDisplayString(
    getColumnInfo(dataTable, entityProperty),
    dataTable.columnData[entityProperty][itemIdx],
  );
  return fieldValue;
}

function actionOpenAsset(
  actionContext: TpActionContext,
  actionDef: TpActionDef,
  dataContext: TpDataContext,
  entityTypeId: string,
  itemId: string,
  api: Api,
) {
  // get the value of the asset identifying property
  const fieldValue = getAssetFieldValue(actionDef, dataContext, entityTypeId, itemId);
  let validAssetTypes = (actionDef as any).validAssetTypes || [];
  if ((actionDef as any).assetType) {
    validAssetTypes = [(actionDef as any).assetType];
  }
  actionContext.openAssetByProperty((actionDef as any).assetProperty, fieldValue, api, validAssetTypes);
}

function actionOpenAssetById(
  actionContext: TpActionContext,
  actionDef: TpActionDef,
  dataContext: TpDataContext,
  entityTypeId: string,
  itemId: string,
  api: Api,
) {
  const assetId = getAssetFieldValue(actionDef, dataContext, entityTypeId, itemId);
  actionContext.openAssetById(assetId);
}

function actionOpenEntity(
  actionContext: TpActionContext,
  actionDef: TpActionDef,
  dataContext: TpDataContext,
  entityTypeId: string,
  itemId: string,
  api: Api,
) {
  // get the value of the asset identifying property
  const currentdataTable = getDataTableByCanonicalType(dataContext, entityTypeId);
  if (!(itemId in currentdataTable.itemIdMap)) {
    showErrorMessage(`Entity not found: ${itemId}`);
  }
  const itemIdx = currentdataTable.itemIdMap[itemId];
  const currentEntityPropertyId = (actionDef as any).currentEntityProperty;
  const currentMatchingEntityPropertyValue = convertColumnDataToDisplayString(
    getColumnInfo(currentdataTable, currentEntityPropertyId),
    currentdataTable.columnData[currentEntityPropertyId][itemIdx],
  );

  const newEntityPropertyId = (actionDef as any).newEntityProperty;
  const newEntityType = (actionDef as any).newEntityType;
  const newDataTable = getDataTableByCanonicalType(dataContext, newEntityType);
  const newDataItemIds = newDataTable.columnData[newDataTable.colItemId];
  const newDataMatchingProperty = convertColumnDataListToDisplayString(
    getColumnInfo(newDataTable, newEntityPropertyId),
    newDataTable.columnData[newEntityPropertyId],
  );
  const newMatchedIds = [];
  for (let rowNr = 0; rowNr < newDataItemIds.length; rowNr++)
    if (newDataMatchingProperty[rowNr] == currentMatchingEntityPropertyValue) newMatchedIds.push(newDataItemIds[rowNr]);
  if (newMatchedIds.length > 1) {
    showErrorMessage(`More than one item found`);
    return;
  }
  if (newMatchedIds.length == 0) {
    showErrorMessage(`No item found`);
    return;
  }
  actionContext.openEntity(newEntityType, newMatchedIds[0]);
}

function actionOpenAssetEntity(
  actionContext: TpActionContext,
  actionDef: TpActionDef,
  dataContext: TpDataContext,
  entityTypeId: string,
  itemId: string,
  api: Api,
) {
  let fieldValue = '';
  if ((actionDef as any).assetProperty) {
    fieldValue = (actionDef as any).assetProperty;
  } else {
    fieldValue = getAssetFieldValue(actionDef, dataContext, entityTypeId, itemId);
  }

  actionContext.openAssetEntity(
    (actionDef as any).assetProperty,
    fieldValue,
    api,
    (actionDef as any).assetType,
    itemId,
  );
}

function dashboardAction(
  actionContext: TpActionContext,
  actionDef: TpActionDef,
  dataContext: TpDataContext,
  entityTypeId: string,
  itemId: string,
  api: Api,
) {
  const dashboardActionDef = actionContext.dashboardContext.dashboardActionDefs[actionDef.actionId];
  if (!dashboardActionDef) throw getUserError(`Invalid dashboard action: ${actionDef.actionId}`);
  actionContext.closeEntityPopup();
  executeDashboardAction(dataContext, actionContext.dashboardContext, dashboardActionDef, actionContext.dispatch, {
    entityId: {
      type: DBACTIONVARTYPES.Value,
      content: itemId,
    },
  });
}

function actionDownloadFile(
  actionContext: TpActionContext,
  actionDef: TpActionDef,
  dataContext: TpDataContext,
  entityTypeId: string,
  itemId: string,
  api: Api,
) {
  let url = (actionDef as any).url;
  if (!url) throw getUserError('Action is missing url parameter');
  const dataTable = getDataTableByCanonicalType(dataContext, entityTypeId);
  url = formatDataTableStringTemplate(url, dataTable, itemId);

  function downloadFile(url: string) {
    const link = document.createElement('a');
    link.href = url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  if ((actionDef as any).fileType == 'attachment') {
    const blobData: PresignedBlobData = {
      method: 'GET',
      blobPath: url,
      isDownload: true,
    };
    api.blob.getPresignedURL(blobData).then((response) => {
      if (!response.signedUrl) {
        showErrorMessage('Failed to get signed URL');
        return;
      }
      downloadFile(response.signedUrl);
    });
  } else {
    downloadFile(url);
  }
}
const actionFactory: { [actionType: string]: any } = {
  openUrl: actionOpenUrl,
  openAsset: actionOpenAsset,
  openAssetById: actionOpenAssetById,
  openEntity: actionOpenEntity,
  openAssetEntity: actionOpenAssetEntity,
  dashboardAction,
  downloadFile: actionDownloadFile,
  openPDF: actionOpenPdf,
};

interface TpActionProps {
  itemId: string;
  dataContext: TpDataContext;
  entityTypeTemplate: SingleEntityTemplate;
  children?: React.ReactNode;
  elementTemplate: any;
  singleEntityViewContext: TpSingleEntityViewContext;
}

interface TpElementTemplate {
  actions: TpActionDef[];
}

const Actions = (props: TpActionProps) => {
  const canonicalTypeId = props.entityTypeTemplate!.entityType;
  const actionButtons = [];
  const dispatch = useAppDispatch();

  const api = useApiContext();

  const queriedAssetsData = useAppSelector(selectQueriedAssets);
  const openAsset = useOpenAsset();

  function openAssetByProperty(propertyId: string, propertyValue: string, api: Api, validAssetTypes: string[]) {
    // This is currently based on the fully fetched client data. @todo: in the future we should query the server for this
    const asset = findAssetByProperty(queriedAssetsData.fetchedAssets, propertyId, propertyValue, validAssetTypes);
    if (!asset) {
      // If we only have one valid asset type, we can query the server for it
      const params: TpAssetQueryParams = { limit: validAssetTypes.length, offset: 0, searchText: propertyValue };
      if (validAssetTypes.length === 1) {
        params.typeFilter = validAssetTypes[0];
      }
      // If multiple Asset Types can have the property, we set a limit to the number of allowed Asset Types.
      // If a property occurs in multiple assets, we will open the first one based on the order of the validAssetTypes array
      api.asset.listAssets(params).then((response) => {
        if (response.assets.length > 0) {
          for (const assetType of validAssetTypes) {
            const foundAsset = response.assets.find((asset) => asset.type === assetType);
            if (foundAsset) {
              dispatch(addNewAsset({ asset: foundAsset }));
              openAsset(foundAsset.id);
              return;
            }
          }
        }
        showErrorMessage('Asset not found');
      });
    } else {
      openAsset(asset.id);
    }
  }

  /**
   *
   * @param assetId
   * @param api
   */
  function openAssetById(assetId: string) {
    openAsset(assetId);
  }

  function openAssetEntity(propertyId: string, propertyValue: string, api: Api, assetType: string, itemId: string) {
    // Define the callback that will be used after opening the asset
    const openCallBack = (asset: TpAssetInfo) => {
      dispatch(
        performUserSelectionAction({
          dataContextId: asset.id,
          canonicalTypeId: canonicalTypeId,
          userSelectionAction: {
            type: TpEnSelectionFilterTypes.MANUAL,
            description: `Related to ${propertyValue}`,
            method: 'replace',
            selectedItemIds: [itemId],
          },
        }),
      );
    };

    // First look if the Asset can be found, if this is empty, call the api to look for the Asset
    const asset = findAssetByProperty(queriedAssetsData.fetchedAssets, propertyId, propertyValue, [assetType]);

    if (!asset) {
      const params: TpAssetQueryParams = { limit: 1, offset: 0, searchText: propertyValue, typeFilter: assetType };
      api.asset.listAssets(params).then((response) => {
        if (response.assets.length == 1) {
          dispatch(addNewAsset({ asset: response.assets[0] }));
          const callBackFunction: TpPostFetchCallback = {
            openEntity: true,
            callback: () => openCallBack(response.assets[0]),
            canonicalTypeId: canonicalTypeId,
            itemId,
          };
          analyticsDataStorage.postFetchCallBacks[response.assets[0].id] = callBackFunction;
          openAsset(response.assets[0].id);
          return;
        } else {
          showErrorMessage('Asset not found');
          return;
        }
      });
    } else {
      const callBackFunction: TpPostFetchCallback = {
        openEntity: true,
        callback: () => openCallBack(asset),
        canonicalTypeId: canonicalTypeId,
        itemId,
      };
      analyticsDataStorage.postFetchCallBacks[asset.id] = callBackFunction;
      openAsset(asset.id);
    }
  }

  const actionContext: TpActionContext = {
    dispatch,
    openAssetByProperty,
    openAssetById,
    closeEntityPopup: props.singleEntityViewContext.closeEntityPopup,
    dashboardContext: props.singleEntityViewContext.dashboardContext,
    openEntity: props.singleEntityViewContext.openEntity,
    openAssetEntity,
  };

  const elementTemplate = props.elementTemplate as TpElementTemplate;

  for (const action of elementTemplate.actions) {
    const actionFunction = actionFactory[action.type];
    const dataTable = getDataTableByCanonicalType(props.dataContext, canonicalTypeId);
    const hasRequiredPropertyValue = entityIsCompatible(
      dataTable,
      props.itemId,
      action.requiredProperty,
      action.requiredPropertyValues,
    );
    if (actionFunction && hasRequiredPropertyValue) {
      actionButtons.push(
        <FancyButton
          iconName={action.icon}
          onClick={() =>
            actionFunction(
              actionContext,
              action,
              props.dataContext,
              props.entityTypeTemplate.entityType,
              props.itemId,
              api,
            )
          }
        >
          {action.name}
        </FancyButton>,
      );
    } else if (!actionFunction) {
      actionButtons.push(<div>Invalid action type: {action.type}</div>);
    }
  }

  if (actionButtons.length == 0) return null;

  return (
    <>
      {actionButtons.map((action, idx) => (
        <React.Fragment key={idx}>
          {action}
          <div style={{ display: 'inline-block', width: '15px' }} />
        </React.Fragment>
      ))}
    </>
  );
};

export default Actions;
